/**
 * Shared Business Zod Validation Schemas
 * Used by both frontend and backend for consistency.
 */
import * as z from 'zod';

import { DATA_LOCALIZATION_REGION_REGEX } from '../enums/dataLocalizationRegion';
import { PAYMENT_LIMITS } from '../payments';

// A business name is the label on a number card in the picker and in the navbar — long enough for "Acme Support — Bengaluru", short enough to render.
export const MAX_BUSINESS_NAME_LENGTH = 100;

export const pauseMarketingTemplatesSchema = z.object({
  pauseMarketingTemplates: z.boolean(),
});

export type PauseMarketingTemplatesType = z.infer<
  typeof pauseMarketingTemplatesSchema
>;

// Heltar-admin-only lever (PUT /admin/orgs/:orgId/advanced-code-editor) —
// deliberately no customer-facing UI or self-serve route.
export const advancedCodeEditorSchema = z.object({
  advancedCodeEditor: z.boolean(),
});

export type AdvancedCodeEditorType = z.infer<typeof advancedCodeEditorSchema>;

// Org-wide settings bag stored in orgs.settings (jsonb).
export interface OrgSettings {
  advancedCodeEditor?: boolean;
}

export const dataLocalizationRegionSchema = z.object({
  dataLocalizationRegion: z
    .string()
    .regex(
      DATA_LOCALIZATION_REGION_REGEX,
      'Must be a 2-letter uppercase ISO country code',
    )
    .nullable(),
});

export type DataLocalizationRegionType = z.infer<
  typeof dataLocalizationRegionSchema
>;

/**
 * WhatsApp Payments configurations (India). Field names are Meta's own, so the
 * `configuration_name` created here is the one an `order_details` message names.
 */
export const PAYMENT_PROVIDERS = [
  'razorpay',
  'payu',
  'zaakpay',
  'upi_vpa',
] as const;

export interface PaymentConfiguration {
  configuration_name: string;
  provider_name?: string;
  status?: string;
  merchant_category_code?: { code: string; description?: string };
  purpose_code?: { code: string; description?: string };
  merchant_vpa?: string;
  provider_mid?: string;
  created_timestamp?: number;
  updated_timestamp?: number;
}

export const paymentConfigurationSchema = z
  .object({
    configuration_name: z
      .string()
      .trim()
      .min(1)
      .max(PAYMENT_LIMITS.configurationName),
    provider_name: z.enum(PAYMENT_PROVIDERS),
    merchant_category_code: z
      .string()
      .regex(/^\d{4}$/, 'merchant_category_code must be 4 digits')
      .optional(),
    purpose_code: z
      .string()
      .regex(/^\d{2}$/, 'purpose_code must be 2 digits')
      .optional(),
    merchant_vpa: z.string().trim().min(1).optional(),
    redirect_url: z.string().url().optional(),
  })
  .strict()
  .refine(
    config => config.provider_name !== 'upi_vpa' || !!config.merchant_vpa,
    { message: 'merchant_vpa is required for upi_vpa', path: ['merchant_vpa'] },
  );

export type PaymentConfigurationType = z.infer<
  typeof paymentConfigurationSchema
>;

export const paymentConfigurationLinkSchema = z
  .object({ redirect_url: z.string().url().optional() })
  .strict();

/**
 * Origin allowlist for the embeddable web widget. Each entry is either an
 * exact origin (`https://giva.in`) or a wildcard subdomain pattern
 * (`https://*.giva.in`). The backend's `matchOrigin` enforces the same
 * shape — keep this schema and that matcher in sync.
 */
export const WEB_WIDGET_ORIGIN_REGEX =
  /^https?:\/\/(\*\.)?[A-Za-z0-9.-]+(:[0-9]+)?\/?$/;

export const webWidgetOriginsSchema = z.object({
  origins: z
    .array(
      z
        .string()
        .min(1)
        .max(255)
        .regex(
          WEB_WIDGET_ORIGIN_REGEX,
          'Each origin must look like https://yoursite.com or https://*.yoursite.com',
        ),
    )
    .max(50, 'Limit 50 origins per business'),
});

export type WebWidgetOriginsType = z.infer<typeof webWidgetOriginsSchema>;
